import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Users, 
  Building2, 
  Ticket, 
  Trophy,
  MapPin,
  Newspaper,
  Briefcase,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-heading text-2xl font-bold">
              <span className="text-primary">MICHEZO</span>
              <span className="text-secondary">NET</span>
            </span>
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/events" className="text-white/70 hover:text-primary transition-colors font-body text-sm" data-testid="nav-events">
              Events
            </Link>
            <Link to="/directory" className="text-white/70 hover:text-primary transition-colors font-body text-sm" data-testid="nav-directory">
              Directory
            </Link>
            <Link to="/login" className="btn-secondary flex items-center justify-center" data-testid="nav-login">
              Login
            </Link>
            <Link to="/signup" className="btn-primary flex items-center justify-center" data-testid="nav-signup">
              Get Started
            </Link>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Mobile Nav */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden pb-6 flex flex-col gap-4"
          >
            <Link to="/events" className="text-white/70 hover:text-primary py-2" onClick={() => setIsOpen(false)}>Events</Link>
            <Link to="/directory" className="text-white/70 hover:text-primary py-2" onClick={() => setIsOpen(false)}>Directory</Link>
            <Link to="/login" className="btn-secondary w-full text-center" onClick={() => setIsOpen(false)}>Login</Link>
            <Link to="/signup" className="btn-primary w-full text-center" onClick={() => setIsOpen(false)}>Get Started</Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
    {/* Background Image */}
    <div 
      className="absolute inset-0 z-0"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
    </div>
    
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl"
      >
        <h1 className="font-heading text-5xl md:text-7xl font-bold leading-tight mb-6">
          <span className="text-white">Africa's Digital</span><br />
          <span className="text-gradient">Sports Ecosystem</span>
        </h1>
        <p className="text-lg md:text-xl text-white/70 font-body mb-8 max-w-xl">
          Empowering federations, clubs, academies, and athletes with world-class 
          sports technology. Manage events, ticketing, and operations in one platform.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/signup" className="btn-primary flex items-center justify-center gap-2" data-testid="hero-get-started">
            Get Started <ChevronRight size={18} />
          </Link>
          <Link to="/events" className="btn-secondary flex items-center justify-center" data-testid="hero-explore-events">
            Explore Events
          </Link>
        </div>
        
        {/* Stats */}
        <div className="mt-16 grid grid-cols-3 gap-8">
          {[
            { value: '500+', label: 'Organizations' },
            { value: '1,200+', label: 'Events Managed' },
            { value: '50K+', label: 'Athletes' }
          ].map((stat, i) => (
            <motion.div 
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1 }}
            >
              <p className="font-heading text-3xl md:text-4xl font-bold text-primary">{stat.value}</p>
              <p className="text-white/50 text-sm font-body">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: Calendar,
      title: 'Event Management',
      description: 'Create and manage tournaments, leagues, and sports events with ease.',
      color: 'primary'
    },
    {
      icon: Building2,
      title: 'Organization Hub',
      description: 'Centralized management for federations, clubs, and academies.',
      color: 'secondary'
    },
    {
      icon: Ticket,
      title: 'Ticketing System',
      description: 'QR-based ticketing with seat selection and gate scanning.',
      color: 'primary'
    },
    {
      icon: Users,
      title: 'Accreditation',
      description: 'Digital passes and access control for events and venues.',
      color: 'secondary'
    },
    {
      icon: Trophy,
      title: 'Tournament Brackets',
      description: 'Automated fixture scheduling and live match updates.',
      color: 'primary'
    },
    {
      icon: MapPin,
      title: 'Facility Booking',
      description: 'Book stadiums, training grounds, and sports facilities.',
      color: 'secondary'
    }
  ];

  return (
    <section className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">Everything You Need to</span><br />
            <span className="text-primary">Power Sports</span>
          </h2>
          <p className="text-white/60 font-body max-w-2xl mx-auto">
            A modular platform designed for the African sports industry. Scale each module independently.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-card border border-border/40 rounded-xl p-8 card-hover group"
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 ${
                feature.color === 'primary' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary'
              } group-hover:scale-110 transition-transform`}>
                <feature.icon size={24} />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-white/60 font-body text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Events Section
const FeaturedEventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events?limit=3`);
        setEvents(response.data);
      } catch (err) {
        // Use placeholder data
        setEvents([
          { id: '1', name: 'Kenya Premier League 2026', sport: 'Football', start_date: '2026-03-15', event_type: 'League' },
          { id: '2', name: 'East Africa Basketball Championship', sport: 'Basketball', start_date: '2026-04-01', event_type: 'Tournament' },
          { id: '3', name: 'Road to SomaBay Qualifier', sport: 'Golf', start_date: '2026-03-20', event_type: 'Qualifier' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <section className="py-24 bg-card/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">Upcoming Events</h2>
            <p className="text-white/60 font-body mt-2">Discover sports events across Africa</p>
          </div>
          <Link to="/events" className="btn-secondary hidden md:flex items-center gap-2" data-testid="view-all-events">
            View All <ChevronRight size={16} />
          </Link>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {events.map((event, i) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card border border-border/40 rounded-xl overflow-hidden card-hover group"
                data-testid={`event-card-${event.id}`}
              >
                <div className="h-40 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <Trophy className="w-16 h-16 text-primary/50" />
                </div>
                <div className="p-6">
                  <span className="text-xs font-body text-primary uppercase tracking-wider">{event.sport}</span>
                  <h3 className="font-heading text-xl font-bold text-white mt-2 mb-3">{event.name}</h3>
                  <div className="flex items-center gap-4 text-white/50 text-sm font-body">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(event.start_date).toLocaleDateString()}
                    </span>
                    <span className="text-primary">{event.event_type}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        <Link to="/events" className="btn-secondary md:hidden w-full mt-8 flex items-center justify-center gap-2">
          View All Events <ChevronRight size={16} />
        </Link>
      </div>
    </section>
  );
};

// SomaBay World Cup Promo Banner
const SomaBayBanner = () => (
  <section className="py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative rounded-2xl overflow-hidden"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1771838556069-f125a34d9c8a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        </div>
        
        <div className="relative py-12 px-8 lg:px-16 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="text-center lg:text-left max-w-xl">
            <span className="inline-block bg-teal-500/20 text-teal-400 px-4 py-1.5 rounded-full text-xs font-bold mb-4 uppercase tracking-wider">
              Featured Event
            </span>
            <h3 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-3">
              SomaBay World Cup 2026
            </h3>
            <p className="text-white/70 font-body mb-2">
              International Golf Tournament • October 11-16 • Soma Bay, Egypt
            </p>
            <p className="text-white/50 font-body text-sm">
              20 countries. 6 days of world-class golf. Red Sea luxury.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to="/somabay" 
              className="bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold px-8 py-3 rounded-full hover:shadow-lg hover:shadow-teal-500/30 transition-all flex items-center justify-center gap-2"
              data-testid="somabay-banner-cta"
            >
              Explore Event <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Directory Preview Section
const DirectorySection = () => {
  const categories = [
    { icon: Building2, label: 'Federations', count: '54' },
    { icon: Users, label: 'Clubs', count: '320' },
    { icon: Trophy, label: 'Academies', count: '180' },
    { icon: MapPin, label: 'Facilities', count: '95' }
  ];

  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              <span className="text-white">Sports Industry</span><br />
              <span className="text-primary">Directory</span>
            </h2>
            <p className="text-white/60 font-body mb-8">
              Discover and connect with sports organizations across Africa. 
              Find federations, clubs, academies, and facilities in one searchable directory.
            </p>
            <Link to="/directory" className="btn-primary inline-flex items-center gap-2" data-testid="explore-directory">
              Explore Directory <ChevronRight size={18} />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card border border-border/40 rounded-xl p-6 text-center card-hover"
                data-testid={`directory-${cat.label.toLowerCase()}`}
              >
                <cat.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                <p className="font-heading text-3xl font-bold text-white">{cat.count}</p>
                <p className="text-white/50 font-body text-sm">{cat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Transform Your<br />Sports Organization?
        </h2>
        <p className="text-white/70 font-body mb-8 max-w-2xl mx-auto">
          Join hundreds of federations, clubs, and academies already using Michezonet 
          to manage their operations.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/signup" className="btn-primary flex items-center justify-center gap-2" data-testid="cta-signup">
            Create Account <ChevronRight size={18} />
          </Link>
          <Link to="/events" className="btn-secondary flex items-center justify-center" data-testid="cta-browse">
            Browse Events
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-card border-t border-border py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        <div>
          <span className="font-heading text-2xl font-bold">
            <span className="text-primary">MICHEZO</span>
            <span className="text-secondary">NET</span>
          </span>
          <p className="text-white/50 font-body text-sm mt-4">
            Africa's digital sports ecosystem platform.
          </p>
        </div>
        
        <div>
          <h4 className="font-heading text-lg font-bold text-white mb-4">Platform</h4>
          <ul className="space-y-2 font-body text-sm">
            <li><Link to="/events" className="text-white/50 hover:text-primary">Events</Link></li>
            <li><Link to="/directory" className="text-white/50 hover:text-primary">Directory</Link></li>
            <li><Link to="/signup" className="text-white/50 hover:text-primary">Get Started</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-heading text-lg font-bold text-white mb-4">Solutions</h4>
          <ul className="space-y-2 font-body text-sm">
            <li><span className="text-white/50">Federations</span></li>
            <li><span className="text-white/50">Clubs</span></li>
            <li><span className="text-white/50">Academies</span></li>
            <li><span className="text-white/50">Facilities</span></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-heading text-lg font-bold text-white mb-4">Resources</h4>
          <ul className="space-y-2 font-body text-sm">
            <li><span className="text-white/50">News & Insights</span></li>
            <li><span className="text-white/50">Jobs Board</span></li>
            <li><span className="text-white/50">Contact</span></li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-border pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-white/40 font-body text-sm">
          © 2026 Michezonet. All rights reserved.
        </p>
        <div className="flex gap-6 text-white/40 font-body text-sm">
          <span>Privacy Policy</span>
          <span>Terms of Service</span>
        </div>
      </div>
    </div>
  </footer>
);

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <FeaturedEventsSection />
      <SomaBayBanner />
      <DirectorySection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
